import type {
	FetchedSelectFilterOption,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { LastModified } from '../../../../../common/constants/schemas/query-params';
import {
	getInvalidDateRange,
	getProducts3PFromConfigForFilter,
} from '../../../../../common/utils/filters';
import { createSingleWithCustomOptionSelectFilter } from '../../../../filters/single-select-with-custom-option-filter';

import { lastUpdatedLabels } from './messages';

const LAST_UPDATED_VALUES = [
	'today',
	'yesterday',
	'past7Days',
	'past30Days',
	'pastYear',
	'custom',
] as const;

type LastUpdatedType = (typeof LAST_UPDATED_VALUES)[number];

const sortCompareFn = (a: FetchedSelectFilterOption, b: FetchedSelectFilterOption) => {
	return (
		LAST_UPDATED_VALUES.indexOf(a.value as LastUpdatedType) -
		LAST_UPDATED_VALUES.indexOf(b.value as LastUpdatedType)
	);
};
const isIsoDate = (str?: string) => {
	if (!str) {
		return false;
	}
	if (!/\d{4}-\d{2}-\d{2}/.test(str)) {
		return false;
	}
	const d = new Date(str);
	return !isNaN(d.getTime());
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Atlas,
	ProductKeys.Confluence,
	ProductKeys.Focus,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Compass,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('lastUpdated');

type CustomValueParserReturnType =
	| {
			to: string;
			from: string;
	  }
	| undefined;

const customValueParser = (
	queryParams: Record<string, string[] | undefined>,
): CustomValueParserReturnType => {
	const { to, from } = queryParams;
	const isToDateValid = isIsoDate(to?.[0]);
	const isFromDateValid = isIsoDate(from?.[0]);
	const toDate = isToDateValid && to?.[0] ? to?.[0] : '';
	const fromDate = isFromDateValid && from?.[0] ? from?.[0] : '';
	const invalidDateRange = getInvalidDateRange(fromDate, toDate);
	if (!invalidDateRange) {
		return { to: toDate, from: fromDate };
	}
};

const generateQueryParamsGetState = <ID extends string>(
	initialState: SelectFilterState<ID>,
	queryParamKey: string,
) => {
	return (queryParams: Record<string, string[]>): SelectFilterState<ID> => {
		const queryParamValue = queryParams[queryParamKey];

		let value: string[] | undefined;
		let customValue: CustomValueParserReturnType | undefined;

		if (Array.isArray(queryParamValue)) {
			value = queryParamValue;

			if (queryParamValue[0] === 'custom') {
				customValue = customValueParser(queryParams);
			}
		}

		return {
			...initialState,
			value: value || initialState.value,
			customValue: customValue || initialState.customValue,
		};
	};
};

export const filter: Readonly<{
	id: 'lastUpdated';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'lastUpdated'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'lastUpdated'>;
	getInitialState: (
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'lastUpdated'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createSingleWithCustomOptionSelectFilter({
	id: 'lastUpdated',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: true,
	universal: true,
	queryParams: {
		key: 'lastModified',
		fetcher: async (_, config) => {
			return LAST_UPDATED_VALUES.map((value) => ({
				trackingKey: value,
				label:
					config.intl?.formatMessage(lastUpdatedLabels[value]) ||
					lastUpdatedLabels[value].defaultMessage,
				value,
				queryParamValue: value,
				product: config.product,
			}));
		},
		customValueParser,
		generateQueryParamsGetState,
	},
	initialOptions: {
		fetcher: async (config) => {
			return [
				{
					trackingKey: 'anytime',
					label:
						config.intl?.formatMessage(lastUpdatedLabels['anytime']) ||
						lastUpdatedLabels['anytime'].defaultMessage,
					value: 'anytime',
					isDefault: true,
					queryParamValue: LastModified.Anytime,
					product: config.product,
				},
				...LAST_UPDATED_VALUES.map((value) => ({
					trackingKey: value,
					label:
						config.intl?.formatMessage(lastUpdatedLabels[value]) ||
						lastUpdatedLabels[value].defaultMessage,
					value,
					queryParamValue: value,
					product: config.product,
				})),
			];
		},
	},
	sortCompareFn,
	customValueFields: ['to', 'from'],
});
